/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import axios from "axios";
import { notification } from "antd";
import { rutaRenovacion } from "./utils/perfil";

// Definición de la URL base con un valor por defecto en caso de que la variable de entorno no esté configurada
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Crear una instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * SEGURIDAD F16: Función para cerrar sesión y redirigir al login
 * Limpia TODAS las credenciales de localStorage e IndexedDB
 */
const handleLogout = async () => {
  // SEGURIDAD F16: Limpiar TODOS los datos de sesión
  localStorage.removeItem("user");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("token");
  localStorage.removeItem("vehiculoPrincipal");

  // SEGURIDAD F16: Limpiar token de IndexedDB (para Service Worker)
  try {
    // Importar dinámicamente para evitar dependencia circular
    const { default: backgroundGPSService } = await import('./services/backgroundGPSService');
    await backgroundGPSService.eliminarTokenAuth();
  } catch (error) {
    console.warn("No se pudo limpiar IndexedDB:", error);
  }

  // Mostrar notificación
  notification.error({
    message: "Sesión expirada",
    description:
      "Tu sesión ha expirado o no tienes autorización. Por favor inicia sesión nuevamente.",
    placement: "topRight",
    duration: 3,
  });

  // Esperar un momento para que se muestre la notificación antes de redirigir
  setTimeout(() => {
    window.location.href = "/login";
  }, 1000);
};

// Interceptor de solicitud para agregar el accessToken
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Una sola renovación a la vez: si varias peticiones reciben 401 al mismo tiempo, todas esperan el
// mismo token nuevo (antes cada una renovaba con el token viejo y las siguientes recibían 401).
let renovacionEnCurso = null;

const renovarToken = (refreshToken) => {
  if (!renovacionEnCurso) {
    // SEGURIDAD F17: ruta específica del perfil activo (conductor o administrador de vehículos)
    renovacionEnCurso = axios
      .post(`${API_BASE_URL}${rutaRenovacion()}`, { refreshToken }, { timeout: 15000 })
      .then((response) => {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          response.data;

        // Actualizar los tokens en localStorage
        localStorage.setItem("accessToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Despachar un evento personalizado para notificar a los componentes
        window.dispatchEvent(new Event("tokens_updated"));

        return newAccessToken;
      })
      .finally(() => {
        renovacionEnCurso = null;
      });
  }
  return renovacionEnCurso;
};

// La sesión solo se da por perdida si el servidor rechaza el token. Un fallo de red o un error del
// servidor al renovar no debe sacar al conductor (antes cerraba sesión con cualquier fallo de cobertura).
const tokenRechazado = (error) => [400, 401, 403].includes(error?.response?.status);

// Interceptor de respuesta para manejar 401 y refrescar tokens
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // No intentar refrescar token en rutas de autenticación
    const authRoutes = [
      '/conductores/login', 
      '/conductor/forgot-password', 
      '/conductor/reset-password', 
      '/conductor/verify-reset-token',
      '/auth/conductor/login',
      '/auth/app/login',
      '/conductor/refresh-token',  // SEGURIDAD F17: Nueva ruta específica para conductores
      '/admin-vehiculo/refresh-token',
    ];

    const isAuthRoute = authRoutes.some(route => originalRequest.url?.includes(route));

    if (isAuthRoute) {
      return Promise.reject(error);
    }

    // Verificar si el error fue un 401 y que no se haya reintentado ya
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        let newAccessToken;
        try {
          newAccessToken = await renovarToken(refreshToken);
        } catch (refreshError) {
          // Cerrar sesión solo si el servidor rechazó el token; con un fallo de red se conserva
          if (tokenRechazado(refreshError)) {
            handleLogout();
          }
          return Promise.reject(refreshError);
        }

        // Actualizar el header Authorization y reintentar la solicitud original
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } else {
        // No hay refreshToken, cerrar sesión
        handleLogout();
        return Promise.reject(error);
      }
    }

    // Si sigue siendo un 401 después de reintentar, cerrar sesión solo si hay una sesión activa
    if (error.response?.status === 401) {
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      
      // Solo hacer logout si había tokens (sesión activa) y no es una ruta de auth
      if ((accessToken || refreshToken) && !isAuthRoute) {
        handleLogout();
      }
      return Promise.reject(error);
    }

    // Manejar errores de red (offline)
    if (!error.response) {
      notification.error({
        message: "Error de Red",
        description:
          "No se pudo conectar con el servidor. Verifica tu conexión a Internet.",
        placement: "topRight",
      });
    }

    // Devolver el error completo para que los componentes puedan manejarlo
    return Promise.reject(error);
  }
);

// Exportar la instancia personalizada de axios
export default api;
